/**
 * Per-space badge colour. Each space maps to a single hue (0-360); lightness and
 * chroma are fixed in CSS (`.space-badge` in src/styles/tokens.css), so every
 * badge has the same visual weight - a spread of hues, never a rainbow of
 * clashing intensities. The hue is the only per-space datum and is passed to CSS
 * via the `--space-badge-hue` custom property.
 *
 * Keys are display names (what the user sees), not machine space names.
 */
export const SPACE_HUE: Record<string, number> = {
  "Test Space": 255,
  Public: 215,
  "ST&S SmartAssistant": 158,
  Dynamic: 38,
  Genie: 330,
  "M&A": 280,
  HSE: 10,
  "S&ORA": 190,
};

// Lower-cased view of SPACE_HUE so the lookup is case-insensitive: callers may
// pass a machine name (e.g. "public") before the title-cased display name has
// loaded, and a pinned space must keep its colour either way.
const NORMALIZED_SPACE_HUE: Record<string, number> = Object.fromEntries(
  Object.entries(SPACE_HUE).map(([name, hue]) => [name.toLowerCase(), hue]),
);

/**
 * Deterministic hue for a space name not in SPACE_HUE: a stable string hash
 * mapped into [0, 360). The same name always yields the same hue across renders
 * and sessions (no randomness, no recompute drift).
 */
export function hueFromName(name: string): number {
  let h = 0;
  for (const c of name) {
    h = (h * 31 + c.charCodeAt(0)) % 360;
  }
  return h;
}

/** Resolve a space name to its hue: pinned value if known, hashed otherwise.
 * Case-insensitive, so the same space always resolves to the same hue whether
 * the caller passes its display name or its machine name. */
export function spaceHue(name: string): number {
  const key = name.toLowerCase();
  return NORMALIZED_SPACE_HUE[key] ?? hueFromName(key);
}

/**
 * Two-letter monogram from a space name. Uses the initials of the first two
 * words, treating `&` as a word separator ("M&A" -> "MA"); falls back to the
 * first two letters for single-word names ("Public" -> "PU"). Unicode-aware, so
 * a non-Latin name ("эксперимент") still yields a monogram rather than a blank
 * chip.
 */
export function monogram(name: string): string {
  const cleaned = name.replace(/[^\p{L}\p{N}& ]/gu, "");
  const words = cleaned.split(/[\s&]+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return cleaned.slice(0, 2).toUpperCase();
}
